import { useCallback, useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/shallow';

import { useCoworkerProfileStore } from '@/store/coworker-preferences.store';
import { FilterType } from '@/types/asset-search.types';
import type { AssetPreview } from '@repo/api-types/customer-assets';
import { API } from '@repo/hooks';
import { getNotifier } from '@repo/utils';
import { useQuery } from '@tanstack/react-query';

import { useSearch } from '@/components/filters/search/search.hooks';

import type { AssetFilterQueryParam } from '@/api/assets.api';
import { getAssets } from '@/api/assets.api';

export const useAssets = () => {
    const notifier = getNotifier();
    const { daysFilter, assetIdFilter, typeFilter } = useSearch();
    const { buCode, coworkerCacheKey } = useCoworkerProfileStore(
        useShallow((state) => ({
            buCode: state.coworkerPreferences?.buCode ?? '',
            coworkerCacheKey: state.msalProfile?.id ?? state.msalProfile?.mailNickname ?? '',
        }))
    );

    // Array of filters that will be used to query the API
    const filters: AssetFilterQueryParam[] = [
        {
            name: FilterType.NoOfDays,
            value: daysFilter,
        },
        {
            name: FilterType.AssetId,
            value: assetIdFilter,
        },
    ];

    // Query the API
    const assetsQuery = useQuery({
        queryKey: [API.assets, { buCode, coworkerCacheKey }, ...filters],
        queryFn: () => getAssets(filters),
        meta: {
            persist: buCode.length > 0 && coworkerCacheKey.length > 0,
        },
        enabled: Boolean(daysFilter),
    });

    // Show errors from the API
    useEffect(() => {
        if (!assetsQuery.data?.errors) {
            return;
        }

        // TODO: this is a workaround to avoid showing errors for quotes because they are not supported yet
        if (assetsQuery.data.errors[0]?.assetType === 'quote') {
            return;
        }

        // TODO: render translated error messages once we have error codes
        for (const { error } of assetsQuery.data.errors) {
            notifier?.add({
                message: error.errorMessage,
                title: 'Error',
                type: 'error',
            });
        }
    }, [assetsQuery.data?.errors, notifier]);

    // Frontend filtering
    const filterAssets = useCallback((filter: FilterType, filterValue: string | string[], assets?: AssetPreview[]) => {
        if (!assets) {
            return [];
        }

        return assets.filter((asset) => {
            switch (filter) {
                case FilterType.Type: {
                    if (Array.isArray(filterValue) && filterValue.length > 0) {
                        return filterValue.includes(asset.assetType.toLowerCase());
                    }
                    return true;
                }
                default: {
                    return true;
                }
            }
        });
    }, []);

    // List of assets that will be rendered after filtering
    const assets = useMemo(() => {
        // Filter assets by type
        const filteredAssets = filterAssets(FilterType.Type, typeFilter, assetsQuery.data?.assets);
        return filteredAssets;
    }, [assetsQuery.data?.assets, typeFilter, filterAssets]);

    // Return the query and the filtered assets
    return { assetsQuery, assets };
};
